import { Controller, useForm, useWatch } from 'react-hook-form';
import { FontIcon } from '@fluentui/react';
import {
  Field,
  InfoLabel,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { OpenFilled } from '@fluentui/react-icons';
import { FormActions, useFormLayoutStyles, useMutation } from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import { ColorField } from '../../common/ColorField';
import {
  updateOrderStatus,
  type IOrderStatus,
} from '../../services/workService';
import {
  orderStatusBody,
  toOrderStatusFormValues,
  type OrderStatusFormValues,
} from './orderStatusBody';

const useStyles = makeStyles({
  preview: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  previewIcon: {
    fontSize: tokens.fontSizeBase600,
  },
});

export interface OrderStatusFormProps {
  status: IOrderStatus;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const OrderStatusForm = ({
  status,
  onClose,
  onSaved,
}: OrderStatusFormProps) => {
  const styles = useFormLayoutStyles();
  const formStyles = useStyles();
  const save = useMutation((values: OrderStatusFormValues, abortController) =>
    updateOrderStatus(
      abortController,
      status.id,
      orderStatusBody(status, values)
    )
  );
  const { control, handleSubmit } = useForm<OrderStatusFormValues>({
    defaultValues: toOrderStatusFormValues(status),
  });
  const preview = useWatch({ control });

  const onSubmit = handleSubmit(async (values) => {
    const outcome = await save.mutate(values);
    if (outcome.status === 'success')
      onSaved(`Order status ${status.name} updated.`);
  });

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {save.error && (
        <MessageBar intent="error">
          <MessageBarBody>
            {getApiErrorMessage(save.error, 'Failed to save order status.')}
          </MessageBarBody>
        </MessageBar>
      )}
      <Field label="Preview">
        <div className={formStyles.preview}>
          <FontIcon
            iconName={preview.icon || 'Info'}
            className={formStyles.previewIcon}
            style={{ color: preview.color }}
          />
          {status.name}
        </div>
      </Field>
      <Controller
        control={control}
        name="color"
        render={({ field }) => (
          <Field label="Color">
            <ColorField
              value={field.value}
              onChange={field.onChange}
              disabled={save.loading}
            />
          </Field>
        )}
      />
      <Controller
        control={control}
        name="icon"
        render={({ field }) => (
          <Field
            label="Icon"
            hint={
              <div>
                <InfoLabel>Use an icon name from flicon.io</InfoLabel>
                <Link href="https://flicon.io" target="_blank">
                  <OpenFilled />
                </Link>
              </div>
            }
          >
            <Input
              value={field.value}
              disabled={save.loading}
              onChange={(_, data) => field.onChange(data.value)}
            />
          </Field>
        )}
      />
      <FormActions
        disabled={save.loading}
        submitting={save.loading}
        submitLabel="Save"
        onClose={onClose}
      />
    </form>
  );
};

export default OrderStatusForm;
